import React from 'react';

export interface IIconButtonConfig {
  icon: string;
  hoverIcon?: string;
  title?: string;
  alt?: string;
  size?: number;
}

/**
 * 创建图标渲染函数
 * 用于统一处理 GiftedChat 等组件需要的图标渲染函数
 *
 * Dark theme: black PNGs get `--idigest-chat-toolbar-icon-filter` (invert)
 * so they stay visible on dark chrome. Hover keeps black circle + invert(1).
 */
export const createIconRenderer = (config: IIconButtonConfig & { withHoverBackground?: boolean }) => {
  const { icon, hoverIcon, title, alt, size = 26, withHoverBackground = false } = config;

  if (withHoverBackground) {
    // 带黑色圆形背景的 hover 图标（两主题都用黑底 + 反色白图标）
    const HoverIconRenderer = () => (
      <div
        className='idigest-chat-toolbar-icon-hover'
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: '#000',
          borderRadius: `${size / 2}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <img
          src={icon}
          title={title}
          alt={alt}
          className='idigest-chat-toolbar-icon'
          style={{
            width: `${size}px`,
            height: `${size}px`,
            filter: 'invert(1)'
          }}
        />
      </div>
    );
    HoverIconRenderer.displayName = 'HoverIconRenderer';
    return HoverIconRenderer;
  }

  // 普通图标或使用 hoverIcon
  const iconSrc = hoverIcon || icon;
  const IconRenderer = () => (
    <img
      src={iconSrc}
      title={title}
      alt={alt}
      className='idigest-chat-toolbar-icon'
      style={{
        width: `${size}px`,
        height: `${size}px`,
        filter: 'var(--idigest-chat-toolbar-icon-filter, none)'
      }}
    />
  );
  IconRenderer.displayName = 'IconRenderer';
  return IconRenderer;
};

/**
 * 创建一对图标渲染函数（普通图标和 hover 图标）
 */
export const createIconPair = (config: IIconButtonConfig & { useHoverBackground?: boolean }) => {
  const { useHoverBackground = false, hoverIcon, ...restConfig } = config;

  const renderIcon = createIconRenderer(config);
  const renderHoverIcon = useHoverBackground
    ? createIconRenderer({ ...restConfig, icon: config.icon, withHoverBackground: true })
    : hoverIcon
      ? createIconRenderer({ ...restConfig, icon: hoverIcon })
      : renderIcon;

  return {
    renderIcon,
    renderHoverIcon
  };
};
